/**
 * Clinical Glass skin — dual-level selection law (pure, testable).
 *
 * The skin shows corpus questions VERBATIM on ONE level: a floating chip
 * stack above the capsule (one chip per line, so even long questions render
 * whole). The capsule itself is SLIM — ask line + controls, no questions
 * (the in-bar row was retired 2026-08-25; see the law below).
 *
 * The law (Fadi, 2026-08-03, amended 2026-08-25): RANK decides WHO shows,
 * LENGTH decides the ORDER — shortest floats highest. Nothing is ever
 * truncated: the stack wraps whole questions.
 */

/**
 * POOL FILTER — the approved law (demo `bluehat-bars.html` partition(),
 * restored 2026-08-05 after two wrong turns):
 *
 * A question longer than this NEVER renders, on ANY surface. Fadi,
 * 2026-08-05, live on ndx: "these long 200-character pills should NEVER
 * show up." An empty bar on a section whose questions all run long is the
 * DESIGNED outcome, not a bug — the fix for such a section is shorter
 * corpus questions, never looser rendering.
 *
 * History, so this doesn't flip a third time: #540 turned this filter off
 * (misdiagnosing the empty-bar bug that was actually the stranded row) and
 * #541 then sent the long questions it admitted to the floating stack —
 * which is how 100ch+ monsters ended up hovering over live pages. Both
 * moves contradicted the approved demo this constant comes from.
 */
export const CGLASS_PILL_CHAR_CAP = 100;

/** Chips floating above the capsule — the SHORTEST of the top window.
 *  3 since 2026-08-06 (Fadi): two read as an afterthought above a full-width
 *  bar; three reads as an offer.
 *  Since 2026-08-12 this is the UPPER-BOUND FALLBACK, not the law: customers
 *  set their own floating count (0–4, fleet default 2 — "too many nudges"
 *  complaints, Brian) and partitionDualLevels takes it as `stackMax`. This
 *  constant only applies when no count is threaded (tests, older callers). */
export const CGLASS_STACK_MAX = 3;
/** Ranked window the stack draws from: rank decides WHO is eligible, length
 *  decides the ORDER (shortest floats highest — small chips occlude the
 *  least page content). */
export const CGLASS_TOP_WINDOW = 6;

export type CGlassLevels = {
  /** Shortest of the top-ranked set — floats above the capsule. */
  stack: string[];
};

/**
 * THE LAW (v5, Fadi 2026-08-25 — supersedes the v4 two-level law):
 *
 *   · CAP IS A POOL FILTER: over-cap questions never render anywhere.
 *   · rank picks the TOP WINDOW; the SHORTEST of the window float above
 *     the capsule, shortest on top.
 *   · THE IN-BAR QUESTION ROW IS RETIRED. The capsule carries no
 *     questions — the bar stays SLIM (ask line + controls only).
 *     Fadi, live on ndx: the row's question fattened the bar for no
 *     engagement gain; the slim bar (his 2026-08-25 screenshot) wins.
 *     This also makes floating = 0 a TRUE off-switch — the old row kept
 *     rendering questions at floating 0, contradicting the dashboard's
 *     "0 turns that surface off" copy. Now 0 means none, anywhere.
 *
 * History, so the row does not come back by accident: v4 (2026-08-05,
 * restored after #540/#541 drifted) split questions across a floating
 * stack AND a nowrap in-bar row ("3 on top, 1 in the bar"). The row was
 * desktop-only, pixel-measured whole-or-drop, and carried a string of
 * live bugs (stranded measurement 2026-08-05, phones empty 2026-08-04,
 * floating-0 leak). Retired deliberately — not lost.
 */
export function partitionDualLevels(
  pool: readonly string[],
  stackMax: number = CGLASS_STACK_MAX,
): CGlassLevels {
  const seen = new Set<string>();
  const cleaned: string[] = [];
  for (const raw of pool) {
    const q = (raw ?? "").replace(/\s+/g, " ").trim();
    if (!q) continue;
    const key = q.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    cleaned.push(q);
  }
  if (cleaned.length === 0 || stackMax <= 0) return { stack: [] };
  const ok = cleaned.filter((q) => q.length <= CGLASS_PILL_CHAR_CAP);
  if (ok.length === 0) return { stack: [] };
  const top = ok.slice(0, CGLASS_TOP_WINDOW);
  const stack = [...top].sort((a, b) => a.length - b.length).slice(0, stackMax);
  return { stack };
}
